import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'

import { cloneDeep, keyBy, groupBy } from 'lodash';
import * as moment from 'moment'
import { Observable } from 'rxjs/Observable';
import { AnonymousSubscription } from 'rxjs/Subscription';
import { map } from 'rxjs/operators';
import { environment } from '~environment';
import {
  MarketChartData,
  LeaderboardChartData,
  Format,
  FormatType,
  MetricChartGroupDescriptions,
  MarketLeaderboardData
} from '~/core/model';
import { KeybindService, KeybindDescription, MarketService, KEYBOARD_CODES } from '~/core/services';

@Component({
  selector: 'leaderboard',
  templateUrl: './leaderboard.component.html',
  styleUrls: ['./leaderboard.component.scss']
})
export class LeaderboardComponent implements OnDestroy, OnInit {
  @Input() marketId: number;
  @Output() close: EventEmitter<void> = new EventEmitter();
  marketLeaderBoardData: Observable<MarketLeaderboardData>;
  subscriptions: AnonymousSubscription[] = [];
  cdnUri = environment.baseCdnUrl;
  imageStyle: string;
  safeImageStyle: SafeUrl;

  constructor(
    private marketService: MarketService,
    private keybindService: KeybindService,
    sanitizer: DomSanitizer
  ) {
    this.subscriptions.push(this.keybindService.bind(new KeybindDescription(KEYBOARD_CODES.escape))
      .subscribe(x => this.onClickedExit()));
    this.safeImageStyle = sanitizer.bypassSecurityTrustStyle(this.imageStyle);
  }

  ngOnInit() {
    const leaderboardChartData: Observable<LeaderboardChartData> = this.marketService.getMarketLeaderboard(this.marketId)
      .pipe(
        map(data => {
          let chartData: MarketChartData[] = [];
          data.metricGroups.map(d => {
            let xAxis: string[] = [];
            let star1: number[] = [];
            let star5: number[] = [];
            let gap: number[] = [];
            d.data
              .sort((a, b) => this.timeSliceToDate(a.timeSlice) - this.timeSliceToDate(b.timeSlice))
              .forEach(t => {
                xAxis.push(moment(this.timeSliceToDate(t.timeSlice)).format('MMM-YY'));
                star1.push(parseFloat(t.star_1.formattedValue));
                star5.push(parseFloat(t.star_5.formattedValue));
                gap.push(parseFloat(t.gap.formattedValue));
              });
            chartData.push({
              chartId: d.chartId,
              title: d.title,
              xAxis: xAxis,
              star1: star1,
              star5: star5,
              gap: gap,
              format: this.parseFormatString(d.formatString),
              class: d.class
            })
          });
          return {
            chartData: chartData,
            marketName: data.marketName,
            marketCode: data.marketCode,
            marketOverview: data.marketOverview
          };
        }));

    const marketChartDescriptions: Observable<MetricChartGroupDescriptions> = this.getMarketChartGroupDescriptions();
    this.marketLeaderBoardData = this.setMarketLeaderboardDescriptions(leaderboardChartData, marketChartDescriptions);
    this.imageStyle = `url(${this.cdnUri}/mkts-leaderboard/${this.marketId}.jpg), url(assets/images/landingMast.jpg)`;
  }

  private getMarketChartGroupDescriptions(): Observable<MetricChartGroupDescriptions> {
    return this.marketService.getMarketLeaderboardDescriptions().pipe(
      map((descriptions) => {
        const metricGroupDescription: MetricChartGroupDescriptions = {
          chartGroup: { ...descriptions },
          metricDescriptions: keyBy([].concat.apply([], descriptions.map(d => d.data)), 'chartId')
        }
        return metricGroupDescription
      })
    );
  }

  private setMarketLeaderboardDescriptions(
    marketChartData: Observable<LeaderboardChartData>,
    marketChartDescriptions: Observable<MetricChartGroupDescriptions>
  ): Observable<MarketLeaderboardData> {
    return Observable.forkJoin(marketChartData, marketChartDescriptions).pipe(
      map(([chartData, descriptions]) => {
        const clonedChartData = cloneDeep(chartData);

        clonedChartData.chartData.forEach((chart) => {
          chart.description = descriptions.metricDescriptions[chart.chartId].longDescription;
          chart.footNoteIndex = descriptions.metricDescriptions[chart.chartId].footNoteIndex;
          chart.metricgroup = descriptions.metricDescriptions[chart.chartId].metricgroup;
        });

        const marketLeaderBoardData: MarketLeaderboardData = {
          ...clonedChartData,
          chartData: groupBy(clonedChartData.chartData, 'metricgroup'),
          chartDescription: keyBy(descriptions.chartGroup, 'metricGroup')
        };
        return marketLeaderBoardData;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  parseFormatString = (formatString: string): Format => {
    let formatType: FormatType;

    if (/%/.test(formatString)) { formatType = 'percentage' }
    if (/\$/.test(formatString)) { formatType = 'currency' }

    const precision = formatString.split('.')[1]

    return {
      formatType: formatType,
      precision: precision && precision.replace('%', '').length
    }
  }

  timeSliceToDate = (timeSlice: string): number => {
    const yearMonth = timeSlice
      .split(/\D+/g)
      .filter(Boolean)
      .map(Number)
    return new Date(yearMonth[0], yearMonth[1] - 1).getTime();
  };

  onClickedExit() {
    this.close.emit();
  }
};



// WEBPACK FOOTER //
// ./src/app/main/views/leaderboard/leaderboard.component.ts